import { Component, Inject, OnInit } from '@angular/core';
import {
	MatDialog,
	MatDialogRef,
	MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import {
	ProceedModalComponent,
	ProceedModalData,
} from '@components/proceed-modal/proceed-modal.component';
import { dollarsToCents } from '@core/util/money';
import { Amendment, AmendmentChangeStatus, Amendments } from '@fnModels/llc';
import { LLCFunctions } from '@functions/llc.service';
import { Address } from '@models/address';
import { BaseClient, BusinessClient, PersonClient } from '@models/clients';
import { LLCTransaction, LLCTransactionData } from '@models/llc-mortgages';
import { Matter, MatterPropertyDetails } from '@models/matter';
import { MatterMortgage } from '@models/mortgage';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { LlcRepoService, Update } from '@repos/mortgages/llc.service';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { groupBy } from 'lodash';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import {
	getLLCLenderType,
	LLCLenderType,
} from 'src/app/shared/services/mortgages/llc.service';
import {
	AddressFields,
	AmendmentModalData,
	AmendmentVM,
	Field,
	Mappings,
	Ownership,
	Section,
	Status,
} from './ammendments.models';
import { updateMatterMortgage } from './ammendments.util';
import {
	SyncAmendmentUsersComponent,
	SyncAmendmentUsersComponentData,
} from './sync-amendment-users/sync-amendment-users.component';
import { TdAcknowledgeModalComponent } from './td-acknowledge-modal/td-acknowledge-modal.component';
import { clientLegalName } from '@core/util/client';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { llcEstateTypeToCloserType } from '@core/util/llc';
import {
	BaseTransactionAddress,
	ClientMappings,
} from '@models/mortgages/mortgage-base';

@Component({
	selector: 'app-amendments',
	templateUrl: './amendments.component.html',
	styleUrls: ['./amendments.component.scss'],
	standalone: false,
})
export class LLCAmendmentsModalComponent implements OnInit {
	public transaction: LLCTransaction;
	public latestTransactionData: LLCTransactionData;
	public ICON = ICON;
	public amendments: AmendmentVM[];
	public loading = true;
	public error = false;
	public lenderRequestId;
	public updating = false;
	public updatingError = false;
	public invalidMappingsString = null;

	public status: Status = {
		updateCloser: false,
		acceptAmendments: false,
		getLatestData: false,
		updateStatus: false,
	};

	@ViewSelectSnapshot(MatterClients.get) clients: (
		| PersonClient
		| BusinessClient
	)[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(MatterMortgagesState.getAllMortgages)
	matterMortgages: MatterMortgage[];
	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	constructor(
		public dialogRef: MatDialogRef<LLCAmendmentsModalComponent>,
		@Inject(MAT_DIALOG_DATA) public _data: AmendmentModalData,
		private dialog: MatDialog,
		private llcFunctions: LLCFunctions,
		private llcRepo: LlcRepoService,
	) {
		this.transaction = _data.transaction;
	}

	async ngOnInit(): Promise<void> {
		this.dialogRef.addPanelClass('ammendments-dialog');
		this.dialogRef.addPanelClass('max-600-width-dialog');
		this.dialogRef.addPanelClass('max-90vh-height-dialog');
		// fetch the latest amendments
		try {
			this.loading = true;
			this.error = false;
			const _amendments = (await this.llcFunctions
				.getAmendments(this.transaction.id)
				.toPromise()) as Amendments;

			LogRocket.log('Mortgage amendments', _amendments);
			this.latestTransactionData = (await this.llcFunctions
				.fetchLatestDataForTransaction(this._data.transaction.id)
				.toPromise()) as LLCTransactionData;

			this.lenderRequestId = _amendments.lenderRequestId;

			this.amendments = _amendments?.lenderChanges?.map((x) =>
				this.buildVM(x, this.transaction),
			);
		} catch (err) {
			console.log('ERROR', err);
			this.error = true;
			LogRocket.error('Unable to fetch amendments from llc', err);
		} finally {
			this.loading = false;
		}
	}

	buildVM = (
		amendment: Amendment,
		transaction: LLCTransaction,
	): AmendmentVM => {
		try {
			const tag = amendment?.tag?.toLowerCase();
			const mapping = Mappings[tag];

			if (!mapping) {
				LogRocket.error(`Amendments: Unable to map tag ${tag} to mapping`);
			}

			const result: AmendmentVM = {
				allowedToOverride: false,
				override: true,
				amendment,
				action: 'modify',
				previousValue: amendment.previousValue,
				newValue: amendment.newValue,
				mapping,
			};

			// if lender field no mappings exists
			if (!mapping || mapping?.ownership === Ownership.LENDER) {
				return result;
			}

			if (mapping.ownership === Ownership.SHARED) {
				result.allowedToOverride = amendment?.actionable ?? false;
				result.override = !amendment?.actionable;

				//is it a client update? If so map clientId
				if (mapping.section === Section.CLIENT) {
					const closerClientMappings = this.findClientMappings(
						amendment.id,
						transaction,
					);
					if (closerClientMappings.length === 1) {
						result.closerClientId = closerClientMappings[0].closerClientId;
					} else {
						// this is going to be a company or signing officer;
						const company = closerClientMappings.find(
							(x) => x.type === 'company',
						);
						const signingOfficer = closerClientMappings.find(
							(x) => x.type === 'signingOfficer',
						);

						if (
							mapping.field === Field.CLIENT_FIRSTNAME ||
							mapping.field === Field.CLIENT_MIDDLENAME ||
							mapping.field === Field.CLIENT_LASTNAME
						) {
							result.closerClientId = signingOfficer.closerClientId;
						} else {
							result.closerClientId = company.closerClientId;
						}
					}
					result.extraLabel = this._getClientName(result.closerClientId);
				}

				return result;
			}

			if (mapping.ownership === Ownership.EXCEPTION) {
				if (amendment.label.toLowerCase().includes('has been added')) {
					result.action = 'add';

					return result;
				}

				result.action = 'remove';
				const closerClientMappings = this.findClientMappings(
					amendment.id,
					this.transaction,
				);

				if (mapping.field === Field.CLIENT) {
					let closerClient;

					const mainClient = closerClientMappings.find(
						(x) => x.type === 'company' || x.type === 'person',
					);

					if (mainClient) {
						closerClient = this.clients.find(
							(x) => x.id === mainClient.closerClientId,
						);
					}

					if (closerClient) {
						const name = clientLegalName(
							closerClient,
							this.nameFormattingSettings,
						);
						result.previousValue = name;
						result.closerClientId = closerClient.id;
					}
				}

				return result;
			}
		} catch (err) {
			console.log('amendments buildVM error', err);
			LogRocket.error('amendments buildVM error', err);
		}
	};

	_getClientName(clientId): string {
		const client = this.clients.find((x) => x.id === clientId);

		if (client) {
			return clientLegalName(client, this.nameFormattingSettings);
		}

		return null;
	}

	findClientMappings(
		llcClientId: string,
		transaction: LLCTransaction,
	): ClientMappings {
		const exists =
			transaction?.data?.mortgagors?.some(
				(x) => x.id === Number(llcClientId),
			) ||
			transaction?.data?.guarantors?.some((x) => x.id === Number(llcClientId));

		if (!exists) {
			return null;
		}

		return transaction.clientMappings.filter(
			(mapping) => mapping.llcId === Number(llcClientId),
		);
	}

	async continue() {
		try {
			LogRocket.log(
				'Accept amendments for llc transaction',
				this._data?.transaction?.id,
				'ammendments',
				this.amendments,
			);

			this.invalidMappingsString = null;
			const invalidMappings = this.amendments?.filter((x) => !x.mapping) ?? [];

			if (invalidMappings?.length > 0) {
				this.invalidMappingsString = invalidMappings
					.map((x) => x?.amendment?.label)
					?.join(', ');
				LogRocket.error(
					"Unable to accept LLC ammendments - we don't have some fields mapped",
					invalidMappings,
				);
				throw new Error(
					`Unable to accept the following ammendments - ${this.invalidMappingsString}`,
				);
			}

			this.updatingError = false;
			this.updating = true;

			const actionableList = this.amendments.filter(
				(x) => x?.mapping && x.override,
			);

			const matterMappings = actionableList.filter(
				(x) => x.mapping.section === Section.MATTER,
			);

			const matterMortgage = actionableList.filter(
				(x) => x.mapping.section === Section.MATTER_MORTGAGE,
			);
			const clientsUpdates = actionableList.filter(
				(x) => x.mapping.section === Section.CLIENT && x.action === 'modify',
			);
			const clientCreate = actionableList.filter(
				(x) =>
					x.mapping.ownership === Ownership.EXCEPTION && x.action === 'add',
			);
			const clientRemove = actionableList.filter(
				(x) =>
					x.mapping.ownership === Ownership.EXCEPTION && x.action === 'remove',
			);
			const propertyMappings = actionableList.filter(
				(x) => x.mapping.section === Section.PROPERTY,
			);

			const updatedMatter = this.updateMatter(matterMappings);
			const updateProperty = this.updateProperty(
				propertyMappings,
				this.transaction.data.properties[0].address,
			);
			const updatedMatterMortgage = updateMatterMortgage(
				this.transaction,
				matterMortgage,
				this.transaction.data.properties[0].mortgagePriority,
				this.matterMortgages,
			);
			const updatedClients = this.updateClient(
				clientsUpdates,
				this.latestTransactionData,
			);

			const acceptRejections = this.buildAcceptRejectAmendments(
				this.amendments,
			);

			LogRocket.log('Amendments: acceptRejections', acceptRejections);

			if (!this.status.acceptAmendments) {
				await this.llcFunctions
					.acceptRejectAmendments(
						this._data.transaction.id,
						this.lenderRequestId,
						acceptRejections,
					)
					.toPromise();
				LogRocket.log('acceptRejectAmendments successful');
				this.status.acceptAmendments = true;
			}

			this.latestTransactionData = (await this.llcFunctions
				.fetchLatestDataForTransaction(this._data.transaction.id)
				.toPromise()) as LLCTransactionData;

			// accept / reject amendments
			if (!this.status.updateCloser) {
				await this.llcRepo.applyAmendments(
					{ id: this.matterId, update: updatedMatter },
					{ id: this.matterId, update: updateProperty },
					updatedMatterMortgage,
					updatedClients.clients,
					updatedClients.clientAddress, // should be update clients
					clientRemove.map((x) => x.closerClientId),
					this.transaction.id,
					this.latestTransactionData,
				);
				this.status.updateCloser = true;
			}

			// if there is a new client then send the user to the user mappings modal
			if (clientCreate?.length > 0) {
				const data: SyncAmendmentUsersComponentData = {
					transactionId: this.transaction.id,
				};
				this.dialog.open(SyncAmendmentUsersComponent, { data });
			}

			// if the lender is TD then rejections are not accepted
			const rejections = acceptRejections.filter((x) => x.status === 'REJECT');
			if (
				getLLCLenderType(this.transaction.lender) === LLCLenderType.TD &&
				rejections?.length > 0
			) {
				LogRocket.log('Open TD Acknowldge component as rejections', rejections);
				this.dialog.open(TdAcknowledgeModalComponent);
			}

			await this.llcFunctions
				.updateTransactionStatus(this.transaction.id)
				.toPromise();
			await this.llcRepo.addAmmendmentHistory(
				this.transaction.id,
				this.amendments,
			);

			this.dialogRef.close();
		} catch (err) {
			this.updatingError = true;
			console.log('Unable to save actionable times', this.status);
			LogRocket.error('Error accepting/rejecting amendments', this.status, err);
			throw err;
		} finally {
			this.updating = false;
		}

		if (!this.status.updateStatus) {
			LogRocket.log('Amemendments update status - false');
			await this.llcFunctions
				.updateTransactionStatus(this._data.transaction.id)
				.toPromise();
			this.status.updateStatus = true;
		}
	}

	buildAcceptRejectAmendments(
		amendments: AmendmentVM[],
	): AmendmentChangeStatus[] {
		const result: AmendmentChangeStatus[] = [];
		LogRocket.log('build accept reject amendments');
		amendments
			.filter((x) => x.amendment.actionable)
			.forEach((am) => {
				const changeStatus: AmendmentChangeStatus = {
					tag: am.amendment.tag,
					id: am.amendment.id,
					status: am.override ? 'ACCEPT' : 'REJECT',
				};
				result.push(changeStatus);
			});

		return result;
	}

	skip() {
		const data: ProceedModalData = {
			primaryText: "Are you sure you'd like to skip this change notification?",
			secondaryText:
				'You will be restricted to read-only access of the LLC portal',
		};
		const ref = this.dialog.open(ProceedModalComponent, { data });
		ref
			.afterClosed()
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					this.dialogRef.close();
				}
			});
	}

	updateMatter(amendments: AmendmentVM[]): Partial<Matter> {
		LogRocket.log('Amendments: Apply matter amendments');
		const updatedMatter: Partial<Matter> = {};

		amendments.forEach((x) => {
			switch (x?.mapping?.field) {
				case Field.MATTER_CLOSING_DATE:
					updatedMatter.closingDate = x.newValue;
					break;
				case Field.MATTER_PURCHASE_PRICE:
					updatedMatter.purchasePrice = dollarsToCents(Number(x.newValue));
					break;
			}
		});

		LogRocket.log(
			'Amendments: After amendments applied to matter',
			updatedMatter,
		);

		return updatedMatter;
	}

	updateClient(
		amendments: AmendmentVM[],
		latestTransactionData: LLCTransactionData,
	): {
		clients: Update<Partial<PersonClient> | Partial<BusinessClient>>[];
		clientAddress: Update<Partial<Address>>[];
	} {
		LogRocket.log('Amendments: UpdateClient');

		const groups = groupBy(amendments, (x) => x.closerClientId);

		const clientUpdates: Update<
			Partial<PersonClient> | Partial<BusinessClient>
		>[] = [];
		const clientPropertyUpdates: Update<Partial<Address>>[] = [];

		Object.entries(groups).forEach(([key, value]) => {
			const llcClient =
				latestTransactionData?.mortgagors?.find((x) => x.id === Number(key)) ??
				latestTransactionData?.guarantors?.find((x) => x.id === Number(key));

			const partialClient: Partial<BaseClient> = {};
			const addressAmmendments = value.filter((x) =>
				AddressFields?.includes(x.mapping.field),
			);

			if (addressAmmendments.length > 0) {
				clientPropertyUpdates.push({
					id: key,
					update: this.updateAddress(value, llcClient?.address),
				});
			}

			value.forEach((amendment) => {
				switch (amendment.mapping.field) {
					case Field.CLIENT_COMPANY_NAME:
						(partialClient as BusinessClient).name = amendment.newValue;
						break;
					case Field.CLIENT_FIRSTNAME:
						(partialClient as PersonClient).firstName = amendment.newValue;
						break;
					case Field.CLIENT_MIDDLENAME:
						(partialClient as PersonClient).middleNames = amendment.newValue;
						break;
					case Field.CLIENT_LASTNAME:
						(partialClient as PersonClient).lastName = amendment.newValue;
						break;
					case Field.CLIENT_PHONE:
						(partialClient as PersonClient).phone = amendment.newValue;
						break;
					case Field.CLIENT_BUSINESS_PHONE:
						(partialClient as PersonClient).work = amendment.newValue;
						break;
				}
			});

			if (Object.values(partialClient).length > 0) {
				clientUpdates.push({ id: key, update: partialClient });
			}
		});

		return { clients: clientUpdates, clientAddress: clientPropertyUpdates };
	}

	updateProperty(
		amendments: AmendmentVM[],
		llcAddress: BaseTransactionAddress,
	): Partial<MatterPropertyDetails> {
		LogRocket.log('Amendments update property');
		const matterPropertyDetails: Partial<MatterPropertyDetails> = {};

		const addressFields = amendments.filter((x) =>
			AddressFields.includes(x.mapping.field),
		);
		const updatedAddress = this.updateAddress(addressFields, llcAddress);

		if (Object.keys(updatedAddress)?.length > 0) {
			matterPropertyDetails.address = updatedAddress as Address;
		}

		amendments.forEach((x) => {
			switch (x.mapping.field) {
				case Field.PROPERTY_ESTATE_TYPE:
					matterPropertyDetails.interestEstate = llcEstateTypeToCloserType(
						x.newValue,
					);
					break;
				case Field.PROPERTY_OTHER_ESTATE_TYPE_DESCRIPTION:
					matterPropertyDetails.interestEstate = llcEstateTypeToCloserType(
						x.newValue,
					);
					break;
				case Field.PROPERTY_OCCUPANCY_TYPE:
					matterPropertyDetails.ownerResidesAtProperty =
						x.newValue.toLowerCase() === 'owner occupied';
					break;
				case Field.PROPERTY_TYPE_NUMBER_OF_UNITS:
					if (Number(x.newValue) > 1) {
						matterPropertyDetails.propertyType.numberOfUnits = Number(
							x.newValue,
						);
						matterPropertyDetails.propertyType.multiUnit = true;
					} else {
						matterPropertyDetails.propertyType.numberOfUnits = Number(
							x.newValue,
						);
						matterPropertyDetails.propertyType.multiUnit = false;
					}
			}
		});

		LogRocket.log(
			'Amendments: update property after amendments',
			matterPropertyDetails,
		);

		return matterPropertyDetails;
	}

	updateAddress(
		amendments: AmendmentVM[],
		llcAddress: BaseTransactionAddress,
	): Partial<Address> {
		const partialAddress: Partial<Address> = {};

		const streetNumber = amendments.find(
			(amendment) =>
				amendment.mapping.field === Field.ADDRESS_STREET_NUMBER &&
				amendment.override,
		);
		const street1 = amendments.find(
			(amendment) =>
				amendment.mapping.field === Field.ADDRESS_STREET1 && amendment.override,
		);
		let newStreet1;

		if (streetNumber && street1) {
			newStreet1 = `${streetNumber?.newValue} ${street1?.newValue}`;
		} else if (streetNumber && !street1) {
			newStreet1 = `${streetNumber?.newValue} ${llcAddress.streetAddress1}`;
		} else if (!streetNumber && street1) {
			newStreet1 = `${street1?.newValue}`;
		}

		if (newStreet1) {
			partialAddress.street = newStreet1;
		}

		const amendmentsWithoutStreetNumAndStreet = amendments.filter(
			(amendment) =>
				amendment.mapping.field !== Field.ADDRESS_STREET_NUMBER &&
				amendment.mapping.field !== Field.ADDRESS_STREET1 &&
				amendment.override,
		);

		amendmentsWithoutStreetNumAndStreet.forEach((amendment: AmendmentVM) => {
			switch (amendment.mapping.field) {
				case Field.ADDRESS_STREET2:
					partialAddress.street2 = amendment.newValue;
					break;
				case Field.ADDRESS_UNIT:
					partialAddress.unit = amendment.newValue;
					break;
				case Field.ADDRESS_CITY:
					partialAddress.city = amendment.newValue;
					break;
				case Field.ADDRESS_PROVINCE:
					partialAddress.province = amendment.newValue;
					break;
				case Field.ADDRESS_COUNTRY:
					partialAddress.country = amendment.newValue;
					break;
				case Field.ADDRESS_POSTALCODE:
					partialAddress.postcode = amendment.newValue;
					break;
			}
		});

		return partialAddress;
	}
}
